import { useEffect, useRef, useState, type RefObject } from 'react';
import type { SearchMode } from '@/api/types';
import { appConfig } from '@/config/app';
import { useT } from '@/i18n/LocaleProvider';
import styles from './Catalog.module.css';
import { isTwoPhaseSearch } from './searchPhases';

interface Props {
  value: string;
  onChange: (query: string, mode: SearchMode) => void;
  inputRef?: RefObject<HTMLInputElement>;
}

/**
 * Поиск с задержкой 300 мс и кнопкой очистки (CAT-1); «AI-искра» слева — из прототипа.
 *
 * Двухфазный поиск (ADR-009): после задержки ввода уходит дешёвый запрос «набор на лету», после паузы —
 * полный «пауза» (обрывок слова бэкенд дописывает словарём каталога), по Enter — «отправлен» (слово
 * считается словом). Пока бэкенд не ответил поиском v3, фаза одна: запрос сразу «отправлен».
 */
export function SearchBar({ value, onChange, inputRef }: Props) {
  const t = useT();
  const [text, setText] = useState(value);
  const committed = useRef(value);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    if (submitTimer.current) clearTimeout(submitTimer.current);
    typingTimer.current = null;
    submitTimer.current = null;
  };

  // Внешнее изменение адреса (сброс, «Назад», подсказка) подхватывается, свой ввод — нет.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => () => clearTimers(), []);

  const commit = (next: string, mode: SearchMode) => {
    committed.current = next;
    onChange(next, mode);
  };

  const handleChange = (next: string) => {
    setText(next);
    clearTimers();
    if (!isTwoPhaseSearch()) {
      typingTimer.current = setTimeout(() => commit(next, 'submitted'), appConfig.searchDebounceMs);
      return;
    }
    typingTimer.current = setTimeout(() => commit(next, 'typing'), appConfig.searchDebounceMs);
    submitTimer.current = setTimeout(
      () => commit(next, 'paused'),
      appConfig.searchDebounceMs + appConfig.searchSubmitPauseMs,
    );
  };

  const submitNow = () => {
    clearTimers();
    commit(text, 'submitted');
  };

  const clear = () => {
    clearTimers();
    setText('');
    commit('', 'submitted');
    inputRef?.current?.focus();
  };

  return (
    <div className={styles.search}>
      <span className={styles.spark} aria-hidden="true">
        <span className={styles.sparkBig}>✦</span>
        <span className={styles.sparkSmall}>✦</span>
      </span>
      <input
        ref={inputRef}
        id="catalog-search"
        type="search"
        className={styles.searchInput}
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitNow();
        }}
        placeholder={t('catalog.search')}
        aria-label={t('nav.catalog')}
        autoComplete="off"
        data-tour="search"
        enterKeyHint="search"
      />
      {text ? (
        <button
          type="button"
          className={styles.searchClear}
          onClick={clear}
          aria-label={t('catalog.clearSearch')}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
